import { logger } from '@logging/logging';
import { type RouteRecordRaw } from 'vue-router';
import { type App, nextTick } from 'vue';
import AccessDenied from '@shared/page/AccessDenied/AccessDenied.vue';
import NotFound from '@shared/page/NotFound/NotFound.vue';
import ServerError from '@shared/page/ServerError/ServerError.vue';
import { type FlattenedPermissions } from '@permission/types';
import { isOnderdeelBeschikbaar, Onderdeel } from '@auth/marktsegmentBeschikbaarheid';
import { useClaimsAwaitable } from '@auth/useClaims';
import { hasPermissionAwaitable } from '@permission/hasPermission';
import adviseurbeheerRoutes from '@crm-lib/routes/adviseurbeheer.routes';
import relatiegegevensRoutes from './relatiegegevens.routes';
import contactRoutes from './contact.routes';
import router from './router';
import instellingenRoutes from './instellingen.routes';

export const routes: Array<RouteRecordRaw> = [
  {
    name: 'Verzekeringskaarten',
    path: '/publiek/contact/verzekeringskaarten',
    meta: {
      title: 'Verzekeringskaarten',
      public: true,
    },
    component: () => import('../contact/paginas/ContactVerzekeringskaarten.vue'),
  },
  {
    path: '/instellingen',
    children: [
      {
        path: '',
        name: 'Instellingen',
        children: [
          ...instellingenRoutes,
          {
            path: 'alerts',
            name: 'crm.instellingen.alerts',
            redirect: {
              name: 'crm.instellingen.alerts.standaard',
            },
            children: [
              {
                path: 'standaardalerts',
                name: 'crm.instellingen.alerts.standaard',
                component: () => import('../standaardalerts/StandaardAlerts.page.vue'),
                meta: {
                  title: 'Standaard alertteksten',
                  permission: 'StandaardAlerts.StandaardAlertBijwerken',
                },
              },
            ],
          },
          {
            path: 'e-mail-authenticatie',
            name: 'crm.instellingen.e-mail-authenticatie',
            component: () => import('../instellingen/EMailAuthenticatie/EMailAuthenticatie.vue'),
            meta: {
              permission: 'Crm.AdviseurInzien',
              title: 'E-mail authenticatie',
            },
          },
          {
            path: 'sepa-machtiging',
            name: 'crm.instellingen.sepa-machtiging',
            component: () => import('../instellingen/SepaMachtiging/SepaMachtiging.vue'),
            meta: {
              permission: 'Crm.AdviseurInzien',
              title: 'SEPA Machtiging',
            },
          },
        ],
        meta: {
          permission: 'Crm.AdviseurInzien',
        },
        component: () => import('../instellingen/InstellingenLayout.vue'),
      },
    ],
  },
  {
    path: '/',
    redirect: '/alerts',
  },
  {
    name: 'branding',
    path: '/branding',
    meta: {
      title: 'Branding',
      permission: 'Inregeling.InregelingInzien',
    },
    component: () => import('../Branding/BrandingBase.vue'),
  },
  {
    name: 'samenwerkingsovereenkomst',
    path: '/samenwerkingsovereenkomst',
    meta: {
      title: 'Samenwerkingsovereenkomst',
    },
    redirect: '/samenwerkingsovereenkomst/ondertekenen',
    component: () => import('../swo/SwoBase.vue'),
    children: [
      {
        name: 'samenwerkingsovereenkomstOndertekenen',
        path: 'ondertekenen',
        meta: { title: 'Samenwerkingsovereenkomst ondertekenen' },
        props: (route) => ({
          beurs: route.query.beurs ? route.query.beurs === 'true' : undefined,
        }),
        component: () => import('../swo/SwoOndertekenen.vue'),
      },
      {
        name: 'samenwerkingsovereenkomstBedankt',
        path: 'bedankt',
        meta: { title: 'Samenwerkingsovereenkomst ondertekend' },
        component: () => import('../swo/SwoBedankt.vue'),
      },
      {
        name: 'samenwerkingsovereenkomstOverzicht',
        path: 'overzicht',
        meta: { title: 'Samenwerkingsovereenkomst overzicht', permission: 'Crm.Adviseurbeheer' },
        component: () => import('../swo/SwoOverzicht.vue'),
      },
    ],
  },
  {
    name: 'alertsOverzicht',
    path: '/alerts',
    meta: {
      title: 'Alerts',
      permission: 'Alerts.AlertInzien',
      onderdeel: Onderdeel.Alerts,
    },
    redirect: '/alerts/polisalerts',
    component: () => import('../PolisAlertsBeheer/PolisAlertBeheerBase.vue'),
    children: [
      {
        path: 'polisalerts',
        name: 'polisAlertsBeheerOverzicht',
        props: (route) => ({
          adviseurnummer: route.query.adviseurnummer
            ? Number(route.query.adviseurnummer)
            : undefined,
        }),
        component: () => import('../PolisAlertsBeheer/PolisAlertsOverzicht.vue'),
        meta: {
          permission: 'Alerts.AlertInzien',
          title: 'Alerts beheren',
        },
      },
      {
        path: 'polisalerts/:relatienummer',
        name: 'polisAlertsBeheer',
        props: (route) => ({
          relatienummer: Number(route.params.relatienummer),
          adviseurnummer: route.query.adviseurnummer
            ? Number(route.query.adviseurnummer)
            : undefined,
        }),
        component: () => import('../PolisAlertsBeheer/PolisAlertsBeheren.vue'),
        meta: {
          permission: 'Alerts.AlertInzien',
          title: 'Alerts beheren',
        },
      },
    ],
  },
  {
    path: '/relatiegegevens',
    name: 'RelatiegegevensBase',
    component: () => import('../relatiegegevens/RelatiegegevensBase.vue'),
    children: [
      {
        path: '',
        name: 'RelatiegegevensOverzicht',
        meta: {
          permission: 'Crm.RelatieInzien',
        },
        component: () => import('../relatiegegevens/overzicht/RelatiegegevensOverzicht.vue'),
      },
      {
        path: ':relatienummer',
        name: 'Relatiegegevens',
        redirect: { name: relatiegegevensRoutes.find((p) => p.meta?.default === true)!.name },
        children: relatiegegevensRoutes,
        meta: {
          permission: 'Crm.RelatieInzien',
        },
        props: (route) => ({
          relatienummer: Number.parseInt(route.params.relatienummer as string, 10),
          routes: relatiegegevensRoutes,
        }),
        component: () => import('../relatiegegevens/RelatiegegevensLayout.vue'),
      },
      {
        name: 'relatiegegevens.details.prolongatiewijzigingen-polis',
        path: ':relatienummer(\\d+)/prolongatiewijzigingen/relatie/:polisnummer?',
        component: () =>
          import(
            `../relatiegegevens/tabs/RelatiegegevensProlongatiewijzigingen.page/RelatiegegevensProlongatiewijzigingen.page.vue`
          ),
      },
      {
        path: 'nieuw',
        name: 'Nieuwe relatie aanmaken',
        meta: {
          permission: 'Crm.RelatieAanmaken',
        },
        props: (route) => ({
          soort: route.query.soort?.toString().toUpperCase(),
          returnTo: route.query.returnTo?.toString(),
        }),
        component: () => import('../relatiegegevens/NieuweRelatie.vue'),
      },
    ],
  },
  {
    path: '/adviseurs',
    children: [
      {
        path: '',
        name: 'AdviseurOverzicht',
        meta: {
          permission: 'Crm.Adviseurbeheer',
        },
        component: () => import('../adviseurs/AdviseurOverzicht.vue'),
      },
      {
        path: ':adviseurnummer',
        name: 'Adviseurgegevens',
        redirect: { name: adviseurbeheerRoutes.find((p) => p.meta?.default === true)!.name },
        children: adviseurbeheerRoutes,
        meta: {
          permission: 'Crm.Adviseurbeheer',
        },
        component: () => import('../adviseurs/adviseurbeheer/AdviseurbeheerLayout.vue'),
      },
    ],
  },
  {
    name: 'contact',
    path: '/contact',
    meta: {
      title: 'Contact',
      permission: 'Crm.GebruikerInzien',
    },
    redirect: '/contact/medewerkers',
    component: () => import('../contact/ContactOverzicht.vue'),
    children: contactRoutes,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
  },
  {
    path: '/geenToegang',
    name: 'NoAccess',
    component: AccessDenied,
  },
  {
    path: '/serverError',
    name: 'ServerError',
    component: ServerError,
  },
];

const crmRouter = {
  install(app: App) {
    routes.forEach((r) => router.addRoute(r));

    router.beforeEach(async (to, from, next) => {
      if (to.name === 'ServerError') {
        sessionStorage.setItem('previousUrl', from.fullPath);
      }

      // Publieke routes skippen permission check
      if (to.meta.public) {
        next();
        return;
      }

      const { permission, onderdeel } = to.meta;
      if (onderdeel) {
        const claims = await useClaimsAwaitable();
        if (!isOnderdeelBeschikbaar(onderdeel, claims.marktsegment)) {
          next({ name: 'NoAccess' });
          return;
        }
      }

      if (permission) {
        const isAuthorised = await hasPermissionAwaitable(permission as FlattenedPermissions);
        logger.debug(`Router permission check:`, permission, isAuthorised);
        if (!isAuthorised) {
          next({ name: 'NoAccess' });
        } else {
          next();
        }
      } else {
        next();
      }
      window.scrollTo(0, 0);
    });

    router.afterEach(async (to) => {
      await nextTick();
      const { title } = to.meta;
      document.title = title ? `${title} - SUREnet` : 'SUREnet';
    });

    router.install(app);
  },
};

export default crmRouter;
export { router };
